// Pure print-routing helpers shared by PrintingUtils and the native cashier
// surfaces.
//
// "Local" printing is the receipt path ONLY when the tenant's printing.method
// is usb, unset, or none — NOT when network (except for a LAN-direct Epson
// tenant, see shouldPrintDirect below). No host has a silent/driverless print
// bridge any more: local now means only the browser's hidden-iframe
// `print()` dialog (see utils/PrintingUtils.js), which WKWebView swallows on
// native — the reason the native cashier surfaces warn instead of attempting
// it. For a network CloudPRNT tenant the backend enqueue + printer poll owns
// printing, so a local print would be a duplicate. Default (method absent) =
// local print, which preserves today's behaviour for every current tenant.
//
// A network tenant's `protocol` distinguishes the two network transports:
// 'cloudprnt' (default, Star) — the backend queue owns printing, the POS
// prints nothing locally. 'epson' — the on-device ePOS-Print client is the
// ONLY thing that can print (see utils/eposClient.js, wired in a later
// task), so the device must print directly instead of deferring to the
// backend queue. `protocol` defaults to 'cloudprnt' when absent so legacy
// tenants with no `protocol` in their stored config are unaffected.

// The protocols this device can dial itself over the LAN. Both are handled
// by printTransport.sendPayload — 'epson' POSTs ePOS-Print XML over HTTP,
// 'escpos' writes raw bytes to port 9100 for printers with no ePOS service
// at all (the TM-T20X and most third-party thermal units). Kept as a list
// rather than an `=== 'epson'` check because that check was the bug: the
// settings probe has always been able to DETECT an escpos printer and
// sendPayload has always been able to REACH one, but this gate did not
// recognise it, so escpos tenants routed to the backend queue and printed
// nothing. A whitelist, not a "not cloudprnt" test: an unknown protocol has
// no transport, and deferring to the queue is the safe answer for it.
export const DIRECT_PRINT_PROTOCOLS = ['epson', 'escpos'];

/**
 * Whether a network-method tenant should print directly from this device
 * (LAN-direct, over ePOS-Print or raw ESC/POS) rather than deferring to the
 * backend queue.
 * @param {string|null|undefined} method - tenant printing method
 * @param {string|null|undefined} protocol - printer protocol ('cloudprnt' | 'epson' | 'escpos'), defaults to 'cloudprnt'
 * @returns {boolean} true only when method is 'network' AND the protocol is one this device can dial
 */
export function shouldPrintDirect(method, protocol = 'cloudprnt') {
    return method === 'network' && DIRECT_PRINT_PROTOCOLS.includes(protocol);
}

/**
 * Whether the POS should do ANY local printing for this tenant method.
 * Network printing is owned entirely by the backend enqueue/poll (CloudPRNT) —
 * the POS prints nothing locally (neither silent nor iframe) for a network
 * CloudPRNT tenant. A network Epson tenant is the exception: the device is
 * the only thing that can print, so it prints directly.
 * @param {string|null|undefined} method - tenant printing method
 * @param {string|null|undefined} protocol - tenant printing protocol ('cloudprnt' | 'epson'), defaults to 'cloudprnt'
 * @returns {boolean} false only when method is 'network' and protocol is not 'epson'; true otherwise (default = local)
 */
export function shouldPrintAtAll(method, protocol = 'cloudprnt') {
    return method !== 'network' || shouldPrintDirect(method, protocol);
}

/**
 * Resolve the OS printer name mapped to a given role for USB routing.
 * @param {object} config - tenant config object (expects config.printing.usb)
 * @param {'receipt'|'kitchen'|'bar'} role
 * @returns {string|null} the mapped printer name, or null when unmapped / no usb / no printing block
 */
export function printerNameForRole(config, role) {
    return config?.printing?.usb?.[role] || null;
}

/**
 * Whether a local print attempt on this host, for this tenant method, would
 * be a dead end — the local-print path WOULD be taken (shouldPrintAtAll) but
 * no shipping host has a silent-print bridge to actually carry it out, so
 * the caller would fall through to PrintingUtils' hidden-iframe fallback.
 *
 * That fallback assumes a real OS print dialog will pop up from
 * `iframe.contentWindow.print()`, which is true in a plain browser tab (the
 * fallback's original target) but NOT inside Capacitor's iOS WKWebView —
 * there the call is a silent no-op: no dialog, no receipt, no error, no
 * signal to the caller at all. Callers that can surface feedback (the F12a-N
 * native cashier surface's lib/transactionFlow.js + lib/pendingOrderFlow.js)
 * use this to skip the dead attempt and tell the cashier instead, rather
 * than silently doing nothing. Network-method tenants are unaffected either
 * way — shouldPrintAtAll already excludes them (the backend enqueue/poll
 * owns printing for those, correctly silent from the POS's point of view).
 * Gated on host.type === 'capacitor' specifically: a plain browser tab's
 * iframe fallback genuinely works, so only Capacitor's WKWebView is dead.
 * @param {string|null|undefined} method - tenant printing method
 * @param {{ type?: string }} host - the active host abstraction
 * @returns {boolean} true only when a local print attempt would be silently dropped
 */
export function localPrintWouldBeSilentlyDropped(method, host) {
    return host?.type === 'capacitor' && shouldPrintAtAll(method);
}
